import React, { useState, useEffect } from 'react';
import {
  FiFolder as Folder,
  FiFile as File,
  FiDownload as Download,
  FiUpload as Upload,
  FiSearch as Search,
  FiGrid as Grid,
  FiList as List,
  FiImage as Image,
  FiFileText as FileText,
  FiCalendar as Calendar,
  FiTag as Tag,
  FiEye as Eye,
  FiX as X,
  FiAlertCircle as AlertCircle,
} from 'react-icons/fi';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import supabase from '../supabase/supabase';
import ClaimDocumentUpload from './ClaimDocumentUpload';

const BUCKET = 'documents';

// ─── Image Preview Modal ─────────────────────────────────────────────────────

const ImagePreviewModal = ({ url, name, onClose }) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="relative max-w-4xl max-h-full" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={onClose}
        className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
      >
        <X size={28} />
      </button>
      <img src={url} alt={name} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
      <p className="text-white text-sm text-center mt-3 opacity-75">{name}</p>
    </div>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const DocumentsView = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [viewMode, setViewMode] = useState('grid');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [sortBy, setSortBy] = useState('date');

  const [imagePreview, setImagePreview] = useState(null); // { url, name }

  // ── Load documents from Supabase ──────────────────────────────────────────

  const loadDocuments = async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const clientId = user?.clientId;
      if (!clientId) {
        setDocuments([]);
        return;
      }

      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      setLoadError('Could not load documents. Please try refreshing the page.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.clientId) loadDocuments();
  }, [user?.clientId]);

  // ── Storage helpers ───────────────────────────────────────────────────────

  const getSignedUrl = async (filePath) => {
    if (!filePath) return null;
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(filePath, 3600);
    if (error) return null;
    return data?.signedUrl || null;
  };

  const isImage = (doc) => {
    const ext = (doc.file_name || '').split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(ext) ||
      (doc.mime_type || '').startsWith('image/');
  };

  // ── View handler ──────────────────────────────────────────────────────────

  const handleView = async (doc) => {
    const url = await getSignedUrl(doc.file_path);
    if (!url) {
      alert('File unavailable — please contact support.');
      return;
    }

    if (isImage(doc)) {
      setImagePreview({ url, name: doc.file_name });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // ── Download handler ──────────────────────────────────────────────────────

  const handleDownload = async (doc) => {
    const url = await getSignedUrl(doc.file_path);
    if (!url) {
      alert('File unavailable — please contact support.');
      return;
    }

    const a = document.createElement('a');
    a.href = url;
    a.download = doc.file_name || 'document';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    loadDocuments();
  };

  const toggleDocumentSelection = (docId) => {
    setSelectedDocuments((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  // ── Filtering / sorting ───────────────────────────────────────────────────

  const categoryOf = (doc) => {
    const dt = (doc.document_type || '').toLowerCase();
    if (dt === 'policy' || dt === 'declarations page') return 'policy';
    if (dt === 'claim form') return 'claim';
    if (dt === 'id card') return 'id-card';
    return 'other';
  };

  const filteredDocuments = documents
    .filter((doc) => {
      const matchesCategory = selectedCategory === 'all' || categoryOf(doc) === selectedCategory;
      const matchesSearch = (doc.file_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (doc.document_type || '').toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name': return (a.file_name || '').localeCompare(b.file_name || '');
        case 'size': return (b.file_size || 0) - (a.file_size || 0);
        case 'type': return (a.document_type || '').localeCompare(b.document_type || '');
        case 'date':
        default: return new Date(b.created_at) - new Date(a.created_at);
      }
    });

  const categories = [
    { id: 'all', label: 'All Documents', icon: Folder, count: documents.length },
    { id: 'policy', label: 'Policy Documents', icon: FileText, count: documents.filter((d) => categoryOf(d) === 'policy').length },
    { id: 'claim', label: 'Claim Documents', icon: File, count: documents.filter((d) => categoryOf(d) === 'claim').length },
    { id: 'id-card', label: 'ID Cards', icon: Tag, count: documents.filter((d) => categoryOf(d) === 'id-card').length },
    { id: 'other', label: 'Other', icon: Calendar, count: documents.filter((d) => categoryOf(d) === 'other').length },
  ];

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '—';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const totalSize = documents.reduce((sum, doc) => sum + (doc.file_size || 0), 0);
  const storageLimit = 5 * 1024 * 1024 * 1024;
  const storagePercentage = (totalSize / storageLimit) * 100;

  const getFileIcon = (doc) => {
    if (isImage(doc)) return Image;
    if ((doc.document_type || '').toLowerCase().includes('policy') ||
        (doc.file_name || '').endsWith('.pdf')) return FileText;
    return File;
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {imagePreview && (
        <ImagePreviewModal
          url={imagePreview.url}
          name={imagePreview.name}
          onClose={() => setImagePreview(null)}
        />
      )}

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Document Center</h1>
            <p className="text-gray-600">Manage all your insurance documents in one place</p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-[#DC143C] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#B01030] transition-colors flex items-center gap-2 shadow-sm"
          >
            <Upload size={20} /> Upload Documents
          </button>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Storage Used</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatFileSize(totalSize)} / {formatFileSize(storageLimit)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-[#DC143C] h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(storagePercentage, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {loadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle size={18} /> {loadError}
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC143C] focus:border-transparent bg-white"
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="size">Sort by Size</option>
              <option value="type">Sort by Type</option>
            </select>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100 text-[#DC143C]' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <Grid size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-gray-100 text-[#DC143C]' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <List size={20} />
              </button>
            </div>
          </div>
        </div>

        {selectedDocuments.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
            <span className="text-blue-900 font-medium">
              {selectedDocuments.length} document{selectedDocuments.length !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedDocuments([])}
              className="text-gray-600 hover:text-gray-800 font-medium"
            >
              Clear Selection
            </button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Categories Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-4">Categories</h3>
            <div className="space-y-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-red-50 text-[#DC143C] border border-red-200'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <category.icon size={20} />
                    <span className="font-medium">{category.label}</span>
                  </div>
                  <span className={`text-sm font-semibold ${selectedCategory === category.id ? 'text-[#DC143C]' : 'text-gray-500'}`}>
                    {category.count}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Quick Stats</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Documents</span>
                  <span className="font-semibold text-gray-900">{documents.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Size</span>
                  <span className="font-semibold text-gray-900">{formatFileSize(totalSize)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Documents Area */}
        <div className="lg:col-span-3">
          {isLoading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="spinner mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your documents...</p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Folder size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No documents found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || selectedCategory !== 'all'
                  ? 'Try adjusting your search or category filter'
                  : 'No documents have been uploaded for your account yet'}
              </p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="bg-[#DC143C] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#B01030] transition-colors inline-flex items-center gap-2"
              >
                <Upload size={20} /> Upload Documents
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredDocuments.map((doc) => {
                const FileIcon = getFileIcon(doc);
                const isSelected = selectedDocuments.includes(doc.id);
                return (
                  <div
                    key={doc.id}
                    className={`bg-white rounded-xl border-2 transition-all duration-200 hover:shadow-lg ${
                      isSelected ? 'border-[#DC143C] shadow-md' : 'border-gray-200'
                    }`}
                    onClick={() => toggleDocumentSelection(doc.id)}
                  >
                    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 border-b border-gray-200 flex items-center justify-center h-40">
                      <FileIcon size={64} className="text-gray-400" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 flex-1">
                          {doc.file_name}
                        </h3>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => { e.stopPropagation(); toggleDocumentSelection(doc.id); }}
                          className="ml-2 mt-1"
                        />
                      </div>
                      <div className="space-y-1 text-xs text-gray-600 mb-3">
                        <div>{doc.document_type || 'Document'}</div>
                        <div>{formatFileSize(doc.file_size)}</div>
                        <div>{format(new Date(doc.created_at), 'MMM dd, yyyy')}</div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                          className="flex-1 text-xs bg-gray-100 text-gray-700 py-2 px-3 rounded hover:bg-gray-200 transition-colors font-medium flex items-center justify-center gap-1"
                        >
                          <Download size={14} /> Download
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleView(doc); }}
                          className="flex-1 text-xs bg-blue-100 text-blue-700 py-2 px-3 rounded hover:bg-blue-200 transition-colors font-medium flex items-center justify-center gap-1"
                        >
                          <Eye size={14} /> View
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) setSelectedDocuments(filteredDocuments.map((d) => d.id));
                          else setSelectedDocuments([]);
                        }}
                        checked={selectedDocuments.length === filteredDocuments.length && filteredDocuments.length > 0}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Size</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Uploaded</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredDocuments.map((doc) => {
                    const FileIcon = getFileIcon(doc);
                    const isSelected = selectedDocuments.includes(doc.id);
                    return (
                      <tr key={doc.id} className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}>
                        <td className="px-4 py-4">
                          <input type="checkbox" checked={isSelected} onChange={() => toggleDocumentSelection(doc.id)} />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <FileIcon size={20} className="text-gray-400 shrink-0" />
                            <div>
                              <div className="font-medium text-gray-900">{doc.file_name}</div>
                              <div className="text-xs text-gray-600">{doc.document_type || 'Document'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-600 uppercase">
                            {(doc.file_name || '').split('.').pop()}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-600">{formatFileSize(doc.file_size)}</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-600">
                            {format(new Date(doc.created_at), 'MMM dd, yyyy')}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleView(doc)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="View"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => handleDownload(doc)}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              title="Download"
                            >
                              <Download size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Upload Documents</h2>
              <button onClick={() => setShowUploadModal(false)} className="p-2 text-gray-500 hover:text-gray-700 transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <ClaimDocumentUpload
                claimId={`doc_${Date.now()}`}
                onUploadComplete={handleUploadComplete}
                maxFiles={20}
              />
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Document Management Tips</h3>
        <div className="grid md:grid-cols-2 gap-4 text-blue-800 text-sm">
          <div className="space-y-2">
            <p><strong>Backup:</strong> Download important documents regularly for offline access</p>
            <p><strong>Security:</strong> All documents are encrypted and securely stored</p>
          </div>
          <div className="space-y-2">
            <p><strong>Search:</strong> Use keywords or document type to quickly find files</p>
            <p><strong>Share:</strong> Easily share documents with your agent when needed</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsView;
